/**
 * Contrato da migração de etiquetas para o schema v2 — épico #323, fase F2
 * (issue #331).
 *
 * A callable `backfillLabelBaixaQuantities` (super-admin) percorre
 * `tenants/{tenantId}/labels` e leva **todo** doc ainda em v1 para o schema v2: os cinco
 * acumuladores, o ledger `baixas[]` com a entrada sintética da §2.5 quando a
 * etiqueta já estava fechada, e `labelSchemaVersion: 2`. É executada uma vez,
 * sob ordem, e é idempotente: a segunda execução termina com `written == 0` —
 * critério do gate G3.
 *
 * Input e output vivem aqui (e não em `schemas.ts`) porque este contrato tem
 * vida curta: quando a migração terminar e a #334 fizer a limpeza, o arquivo sai
 * inteiro em vez de deixar resíduo num módulo de 4 mil linhas.
 */
import { z } from 'zod';

/**
 * Id da execução: `bf-<yyyyMMdd-HHmm>-<hash6>`. Vai no doc migrado
 * (`_migrationRunId`, consumido pelo trigger), em cada entrada sintética e,
 * pela mão do trigger, em `daily_ops.byProduct.*.lastMigrationRunId`. Se um dia
 * aparecer divergência histórica, prova-se qual execução a produziu — e a
 * correção pode mirar só as entradas daquele run.
 */
export const MIGRATION_RUN_ID_REGEX = /^bf-\d{8}-\d{4}-[a-z0-9]{6}$/;

export const BackfillMigrationRunIdSchema = z
  .string()
  .min(1)
  .max(60)
  .regex(MIGRATION_RUN_ID_REGEX, 'migrationRunId fora do formato bf-<yyyyMMdd-HHmm>-<hash6>');

export const BackfillLabelBaixaQuantitiesInputSchema = z.object({
  /** Sem `tenantId` a varredura é o collection group inteiro (§6.5: um tenant
   *  por execução no começo, geral no fim). */
  tenantId: z.string().min(1).max(120).optional(),
  /** `true` conta o que seria escrito, sem escrever nada. Sempre primeiro. */
  dryRun: z.boolean(),
  /** Path completo do último doc da página anterior (`tenants/x/labels/y`). */
  cursor: z.string().min(1).max(1500).optional(),
  /** Obrigatório ao continuar por cursor: a continuação é o MESMO run. */
  migrationRunId: BackfillMigrationRunIdSchema.optional(),
  /** Teto de páginas nesta chamada (o padrão é o que couber no timeout). */
  maxPages: z.number().int().positive().max(500).optional(),
});
export type BackfillLabelBaixaQuantitiesInput = z.infer<
  typeof BackfillLabelBaixaQuantitiesInputSchema
>;

/**
 * Doc que a migração não escreve porque escrever seria pior do que não escrever.
 * Não é erro de execução — é achado, e vai para a issue.
 *
 * - `MIXED_IN_V1`: `MIXED` só nasce na F3. Num doc v1 é bug de outra ponta, e
 *   não há composição conhecida para materializar.
 * - `SYNTHETIC_ID_INVALID`: `legacy-<labelId>` estourou os 40 caracteres do
 *   contrato ou usou caractere fora do charset. Gravar assim faria o doc
 *   reprovar no `safeParse` do web e a etiqueta sumir da lista em silêncio (o
 *   bug que a F0 corrigiu) — e um ponto no id vira nome de campo inválido em
 *   `movementReceivedAtMillis`, o que trava o trigger em retry.
 * - `UNKNOWN_STATUS`: status fora do ciclo de vida conhecido.
 */
export const BackfillAnomalyReason = {
  MIXED_IN_V1: 'MIXED_IN_V1',
  SYNTHETIC_ID_INVALID: 'SYNTHETIC_ID_INVALID',
  UNKNOWN_STATUS: 'UNKNOWN_STATUS',
} as const;
export type BackfillAnomalyReason =
  (typeof BackfillAnomalyReason)[keyof typeof BackfillAnomalyReason];

export const BackfillAnomalySchema = z.object({
  path: z.string(),
  reason: z.enum([
    BackfillAnomalyReason.MIXED_IN_V1,
    BackfillAnomalyReason.SYNTHETIC_ID_INVALID,
    BackfillAnomalyReason.UNKNOWN_STATUS,
  ]),
  detail: z.string().default(''),
});
export type BackfillAnomaly = z.infer<typeof BackfillAnomalySchema>;

export const BackfillLabelBaixaQuantitiesOutputSchema = z.object({
  migrationRunId: z.string(),
  tenantId: z.string().nullable(),
  dryRun: z.boolean(),
  /** Docs lidos nesta chamada. */
  scanned: z.number().int().nonnegative(),
  /** Docs que receberam patch (0 em `dryRun`; `writable` diz quantos receberiam). */
  written: z.number().int().nonnegative(),
  /** Docs que o patch alcançaria — igual a `written` fora do `dryRun`. */
  writable: z.number().int().nonnegative(),
  /** Já em v2 com os nove campos do contrato: intocados. */
  skippedAlreadyMigrated: z.number().int().nonnegative(),
  /** Etiquetas legadas sem quantidade: acumuladores zerados, ledger vazio (I5). */
  quantityZero: z.number().int().nonnegative(),
  /** Fechadas que ganharam entrada sintética, por status. */
  byStatus: z.record(z.string(), z.number().int().nonnegative()),
  /**
   * Docs sem `responsibleId`: o trigger descarta o delta inteiro
   * (`computeDailyOpsDeltas`), então eles NÃO reconstroem `daily_ops`. É a
   * divergência 3 de `docs/label-counters.md` — contada para que a auditoria
   * dos agregados explique o buraco em vez de descobri-lo.
   */
  withoutResponsible: z.number().int().nonnegative(),
  anomalies: z.array(BackfillAnomalySchema),
  errors: z.array(z.object({ path: z.string(), message: z.string() })),
  /** Onde parar/continuar. `null` quando acabou. */
  cursor: z.string().nullable(),
  done: z.boolean(),
  pages: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
});
export type BackfillLabelBaixaQuantitiesOutput = z.infer<
  typeof BackfillLabelBaixaQuantitiesOutputSchema
>;
